import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';
import { useSxmAutomationConfig } from '@/hooks/useSxmAutomationConfig';
import api from '@/utils/api';
import {
    Loader2,
    Save,
    AlertCircle,
    Mail,
    Calendar,
    ChevronDown,
    Zap,
    TrendingUp,
    Equal,
    RotateCcw,
    ArrowRight
} from 'lucide-react';
import { Button, Input, Badge } from '@/components/ui';
import { FallbackAction, AutomationConfig } from '../types/config';

// --- PATTERN DEFINITIONS ---
type PatternType = 'every_2_days' | 'every_3_days' | 'exponential' | 'aggressive' | 'custom';

interface Pattern {
    id: PatternType;
    name: string;
    description: string;
    icon: ReactNode;
    color: string;
    generate: (count: number) => number[];
}

const PATTERNS: Pattern[] = [
    {
        id: 'every_2_days',
        name: 'Standard Outreach',
        description: 'Consistent 2-day cadence',
        icon: <Equal className="w-4 h-4" />,
        color: 'slate',
        generate: (count) => Array(count).fill(0).map((_, i) => i === 0 ? 0 : 2),
    },
    {
        id: 'every_3_days',
        name: 'Conservative Nurture',
        description: 'Low-frequency sequence',
        icon: <Equal className="w-4 h-4" />,
        color: 'slate',
        generate: (count) => Array(count).fill(0).map((_, i) => i === 0 ? 0 : 3),
    },
    {
        id: 'exponential',
        name: 'Adaptive Cadence',
        description: 'Progressive intervals',
        icon: <TrendingUp className="w-4 h-4" />,
        color: 'blue',
        generate: (count) => {
            const intervals = [0];
            for (let i = 1; i < count; i++) {
                intervals.push(Math.pow(2, i - 1));
            }
            return intervals;
        },
    },
    {
        id: 'aggressive',
        name: 'High-Velocity Sales',
        description: 'Daily touchpoints',
        icon: <Zap className="w-4 h-4" />,
        color: 'blue',
        generate: (count) => Array(count).fill(0).map((_, i) => i === 0 ? 0 : 1),
    },
];

// Helper to detect current pattern
const detectPattern = (intervals: number[]): PatternType => {
    for (const pattern of PATTERNS) {
        const generated = pattern.generate(intervals.length);
        if (JSON.stringify(generated) === JSON.stringify(intervals)) {
            return pattern.id;
        }
    }
    return 'custom';
};

// Convert intervals to absolute days for display
const getAbsoluteDays = (intervals: number[]): number[] => {
    let cumulative = 0;
    return intervals.map(interval => {
        cumulative += interval;
        return cumulative;
    });
};

export const ConfigurationTab: FC = () => {
    const { config, loading, error, updateConfig, resetConfig, isUpdating } = useSxmAutomationConfig();

    const [formData, setFormData] = useState<AutomationConfig | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [calendarConnected, setCalendarConnected] = useState(false);

    useEffect(() => {
        const checkCalendar = async () => {
            try {
                const { data } = await api.get('/auth/google-calendar/status');
                setCalendarConnected(data.connected);
            } catch (err) {
                console.error("Failed to check calendar status", err);
                setCalendarConnected(false);
            }
        };
        checkCalendar();
    }, []);

    useEffect(() => {
        if (config) {
            setFormData(config);
            setIsDirty(false);
        }
    }, [config]);

    const handleChange = <K extends keyof AutomationConfig>(field: K, value: AutomationConfig[K]) => {
        if (!formData) return;
        setFormData(prev => prev ? { ...prev, [field]: value } : prev);
        setIsDirty(true);
        setValidationError(null);
    };

    const handlePatternSelect = (patternId: PatternType) => {
        if (!formData) return;
        const pattern = PATTERNS.find(p => p.id === patternId);
        if (!pattern) return;

        const newIntervals = pattern.generate(formData.maxAttempts);
        handleChange('attemptIntervalsDays', newIntervals);
    };

    const handleMaxAttemptsChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (!formData) return;
        const newMax = parseInt(e.target.value);
        if (isNaN(newMax)) return;

        const currentPattern = detectPattern(formData.attemptIntervalsDays);
        const pattern = PATTERNS.find(p => p.id === currentPattern);

        let newIntervals: number[];
        if (pattern) {
            newIntervals = pattern.generate(newMax);
        } else {
            const current = [...formData.attemptIntervalsDays];
            if (newMax > current.length) {
                newIntervals = [...current, ...Array(newMax - current.length).fill(2)];
            } else {
                newIntervals = current.slice(0, newMax);
            }
        }

        setFormData(prev => prev ? { ...prev, maxAttempts: newMax, attemptIntervalsDays: newIntervals } : prev);
        setIsDirty(true);
    };

    const handleSave = async () => {
        if (!formData) return;

        if (formData.bookingEnabled && !calendarConnected) {
            setValidationError('Please connect your Google Calendar in Profile settings to enable booking.');
            return;
        }
        try {
            await updateConfig(formData);
            setIsDirty(false);
        } catch (err) {
            console.error("Failed to save config", err);
        }
    };

    const handleReset = async () => {
        if (!confirm('This will reset your sequence and window to system defaults. Continue?')) return;

        try {
            await resetConfig();
            setIsDirty(false);
        } catch (err) {
            console.error("Failed to reset config", err);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-32 space-y-4">
            <Loader2 className="w-10 h-10 text-slate-900 animate-spin" />
            <p className="text-slate-400 font-medium animate-pulse uppercase tracking-[0.2em] text-[10px]">Synchronizing System...</p>
        </div>
    );

    if (error || !formData) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-6 max-w-md mx-auto text-center">
            <div className="w-12 h-12 rounded-md bg-red-50 flex items-center justify-center text-red-500 border border-red-100">
                <AlertCircle className="w-6 h-6" />
            </div>
            <div>
                <h3 className="text-lg font-bold text-slate-900">Connection Error</h3>
                <p className="text-sm text-slate-500 mt-2 font-medium">We encountered an issue while loading your configuration.</p>
            </div>
            <Button onClick={() => window.location.reload()} variant="ghost" size="sm" className="px-8 border border-slate-200">
                Try Again
            </Button>
        </div>
    );

    const currentPatternId = detectPattern(formData.attemptIntervalsDays);
    const absoluteDays = getAbsoluteDays(formData.attemptIntervalsDays);

    return (
        <div className="w-full pb-24 px-6 pt-4">

            {/* HEADER - CLEAN & MINIMALIST */}
            <div className="flex flex-col md:flex-row md:items-center justify-between py-6 mb-8 gap-4 border-b border-slate-200 sticky top-0 bg-white/90 backdrop-blur-sm z-40">
                <div>
                    <h1 className="text-xl font-bold text-slate-900 tracking-tight">Configuration Settings</h1>
                    <p className="text-[13px] text-slate-500 mt-1">Manage outreach cadence, business hours, and post-campaign handling.</p>
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        onClick={handleReset}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-900 font-medium"
                    >
                        <RotateCcw className="w-3.5 h-3.5 mr-2" />
                        Reset to Defaults
                    </Button>

                    <Button
                        onClick={handleSave}
                        disabled={!isDirty || isUpdating}
                        size="sm"
                        className={`font-semibold px-6 ${isDirty ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                    >
                        {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
                        Save Changes
                    </Button>
                </div>
            </div>

            {validationError && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-none text-[13px] text-red-700 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <span className="font-bold">Action Required:</span> {validationError}
                        {validationError.includes('Google Calendar') && (
                            <div className="mt-2">
                                <a href="/profile?tab=calendar" className="inline-flex items-center font-bold text-xs uppercase tracking-wider text-red-700 hover:text-red-900 border-b-2 border-red-200 py-0.5">
                                    Link your calendar now <ArrowRight className="w-3 h-3 ml-1" />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">

                {/* LEFT COLUMN: MAIN SEQUENCE ENGINE (spans 8) */}
                <div className="xl:col-span-8 space-y-8">

                    {/* CARD: EMAIL CADENCE */}
                    <div className="panel-white rounded-lg p-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
                            <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center text-white shrink-0">
                                <Mail className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900">Email Sequence</h2>
                                <p className="text-[13px] text-slate-500 font-medium">Set the volume and timing of automated attempts.</p>
                            </div>

                            <div className="ml-auto flex items-center gap-4 bg-slate-50 border border-slate-200 px-4 py-2 rounded-md">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Sequence Length</span>
                                <div className="relative">
                                    <select
                                        value={formData.maxAttempts}
                                        onChange={handleMaxAttemptsChange}
                                        className="appearance-none bg-transparent text-slate-900 text-[13px] font-bold pr-8 cursor-pointer focus:outline-none"
                                    >
                                        {Array.from({ length: 10 }, (_, i) => (
                                            <option key={i} value={i + 1}>{i + 1} Step{i > 0 ? 's' : ''}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* Pattern Grid */}
                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none">Timing Protocols</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {PATTERNS.map((pattern) => {
                                    const isActive = currentPatternId === pattern.id;
                                    return (
                                        <button
                                            key={pattern.id}
                                            onClick={() => handlePatternSelect(pattern.id)}
                                            className={`relative p-4 rounded-md border text-left transition-all ${isActive ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900/5' : 'border-slate-100 bg-white hover:border-slate-300'
                                                }`}
                                        >
                                            <div className={`w-8 h-8 rounded flex items-center justify-center transition-all mb-3 ${isActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                {pattern.icon}
                                            </div>
                                            <h3 className="text-xs font-bold text-slate-900 mb-1">{pattern.name}</h3>
                                            <p className="text-[10px] font-medium text-slate-500 leading-normal">{pattern.description}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Sequence Timeline */}
                        <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center gap-3">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Deployment Schedule</span>
                                </div>
                                {currentPatternId === 'custom' && (
                                    <Badge variant="warning" size="sm" className="rounded-sm font-bold text-[9px]">MANUAL OVERRIDE</Badge>
                                )}
                            </div>

                            <div className="relative overflow-x-auto pb-4">
                                <div className="flex items-end min-w-max h-28">
                                    {absoluteDays.map((day, index) => (
                                        <div key={index} className="flex items-end">
                                            <div className="flex flex-col items-center">
                                                <span className="text-[9px] font-bold text-slate-400 mb-2">STEP {index + 1}</span>
                                                <div className="relative">
                                                    <div className={`w-12 border-x border-t transition-all ${index === 0 ? 'bg-slate-900 border-slate-900 h-16' : 'bg-white border-slate-300 h-12'}`} />
                                                    <div className="absolute inset-x-0 bottom-2 text-center">
                                                        <span className={`text-[10px] font-bold ${index === 0 ? 'text-white' : 'text-slate-900'} text-mono`}>{day}d</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {index < absoluteDays.length - 1 && (
                                                <div className="w-8 h-[1px] bg-slate-200 mb-6 flex items-center justify-center">
                                                    <span className="text-[9px] -top-3 absolute font-semibold text-slate-400">+{formData.attemptIntervalsDays[index + 1]}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECTION: POST-CAMPAIGN */}
                    <div className="panel-white rounded-lg p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center text-white">
                                <RotateCcw className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900">Post-Campaign Protocols</h2>
                                <p className="text-[13px] text-slate-500 font-medium">Define automated actions following the final touchpoint.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <button
                                onClick={() => handleChange('fallbackAction', FallbackAction.MARK_INACTIVE)}
                                className={`p-4 rounded-md border text-left transition-all ${formData.fallbackAction === FallbackAction.MARK_INACTIVE
                                    ? 'border-slate-800 bg-slate-800 text-white'
                                    : 'border-slate-100 bg-white hover:border-slate-300'
                                    }`}
                            >
                                <h3 className="text-xs font-bold mb-1">Archive Lead</h3>
                                <p className={`text-[11px] leading-relaxed ${formData.fallbackAction === FallbackAction.MARK_INACTIVE ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Move leads to 'Inactive' status automatically after final attempt.
                                </p>
                            </button>

                            <button
                                onClick={() => handleChange('fallbackAction', FallbackAction.ESCALATE_TO_HUMAN)}
                                className={`p-4 rounded-md border text-left transition-all ${formData.fallbackAction === FallbackAction.ESCALATE_TO_HUMAN
                                    ? 'border-slate-800 bg-slate-800 text-white'
                                    : 'border-slate-100 bg-white hover:border-slate-300'
                                    }`}
                            >
                                <h3 className="text-xs font-bold mb-1">Flag for Review</h3>
                                <p className={`text-[11px] leading-relaxed ${formData.fallbackAction === FallbackAction.ESCALATE_TO_HUMAN ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Notify team members to manually intervene and continue outreach.
                                </p>
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: BOOKING & OPS (spans 4) */}
                <div className="xl:col-span-4 space-y-8">

                    {/* CARD: MEETING SCHEDULER */}
                    <div className="panel-white rounded-lg p-6">
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center text-white">
                                    <Calendar className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-slate-900">Meeting Booking</h2>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                                        {formData.bookingEnabled ? 'ACTIVE SERVICE' : 'SERVICE OFF'}
                                    </span>
                                </div>
                            </div>

                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={formData.bookingEnabled}
                                    onChange={(e) => handleChange('bookingEnabled', e.target.checked)}
                                />
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                            </label>
                        </div>

                        {!formData.bookingEnabled && (
                            <div className="py-8 px-4 rounded-md bg-slate-50 border border-dashed border-slate-200 text-center">
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-loose">
                                    Automated Meeting Scheduling <br /> is currently disabled
                                </p>
                            </div>
                        )}

                        {formData.bookingEnabled && (
                            <div className="space-y-6">
                                {!calendarConnected && (
                                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-md">
                                        <p className="text-[12px] text-slate-600 font-medium mb-3">
                                            Availability validation requires a Google account connection.
                                        </p>
                                        <button
                                            onClick={() => window.location.href = '/profile?tab=calendar'}
                                            className="w-full bg-slate-900 text-white font-bold text-[11px] h-9 rounded flex items-center justify-center uppercase tracking-widest"
                                        >
                                            Connect Calendar
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">Availability Range</span>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase">Start Time</label>
                                            <Input
                                                type="time"
                                                value={formData.businessHoursStart}
                                                onChange={(e) => handleChange('businessHoursStart', e.target.value)}
                                                className="bg-white border-slate-200 text-slate-900 text-[13px] font-bold h-10 rounded-md"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase">End Time</label>
                                            <Input
                                                type="time"
                                                value={formData.businessHoursEnd}
                                                onChange={(e) => handleChange('businessHoursEnd', e.target.value)}
                                                className="bg-white border-slate-200 text-slate-900 text-[13px] font-bold h-10 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Slot Increments</label>
                                    <div className="relative">
                                        <select
                                            value={formData.slotDuration || 30}
                                            onChange={(e) => handleChange('slotDuration', parseInt(e.target.value))}
                                            className="w-full appearance-none bg-white border border-slate-200 text-slate-900 text-[13px] font-bold py-2.5 px-4 rounded-md cursor-pointer focus:outline-none focus:ring-1 focus:ring-slate-900"
                                        >
                                            {[15, 30, 45, 60, 90, 120].map(mins => (
                                                <option key={mins} value={mins}>{mins} Minutes</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>


                </div>
            </div>

            {/* FLOATING SAVE BAR FOR MOBILE */}
            <div className={`md:hidden fixed bottom-6 inset-x-6 z-50 transition-all duration-500 transform ${isDirty ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
                <Button
                    onClick={handleSave}
                    disabled={isUpdating}
                    className="w-full bg-slate-900 h-12 rounded-md shadow-xl text-white font-bold text-sm uppercase tracking-widest"
                >
                    {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Setup'}
                </Button>
            </div>
        </div >
    );
};
